import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UseDataProps } from './types';
import { LogLevel } from '90.quickConnect.Models/enums';
import { useStore } from '30.quickConnect.Stores';

const DEBUG_ACTIVATION_CLICK_COUNT = 7;
const DEBUG_ACTIVATION_CLICK_DELAY_MS = 500;

const useData = (): UseDataProps => {
  const { t } = useTranslation('userSetting');
  const {
    UserSettingsStore: { qcDebugMode, originalLogLevel, setQcDebugMode },
  } = useStore();

  // Affiche le nom de l'environnement dans le texte de version (sauf en prod)
  const envSuffix = process.env.REACT_APP_ENV !== 'prod' ? ` ${process.env.REACT_APP_ENV}` : '';

  const [isInfoBubbleOpen, setIsInfoBubbleOpen] = useState<boolean>(false);
  const [infoBubbleMessage, setInfoBubbleMessage] = useState<string>('');

  // Compteur de clics rapides - stocké en ref pour ne pas déclencher de re-render
  const clickCountRef = useRef<number>(0);

  // Timestamp du dernier clic - permet de détecter les pauses > DEBUG_ACTIVATION_CLICK_DELAY_MS
  const lastClickTimeRef = useRef<number>(0);

  const showInfoBubble = useCallback((message: string) => {
    setInfoBubbleMessage(message);
    setIsInfoBubbleOpen(true);
  }, []);

  const closeInfoBubble = useCallback(() => {
    setIsInfoBubbleOpen(false);
  }, []);

  /**
   * Gère les clics sur le texte de version.
   * - Réinitialise le compteur si la pause entre clics dépasse DEBUG_ACTIVATION_CLICK_DELAY_MS.
   * - Bascule le mode debug après DEBUG_ACTIVATION_CLICK_COUNT clics consécutifs.
   */
  const handleVersionClick = useCallback(() => {
    const now = Date.now();

    // Trop de temps écoulé depuis le dernier clic -> on repart de zéro
    if (now - lastClickTimeRef.current > DEBUG_ACTIVATION_CLICK_DELAY_MS) {
      clickCountRef.current = 0;
    }
    lastClickTimeRef.current = now;
    clickCountRef.current += 1;
    const remainingClicks = DEBUG_ACTIVATION_CLICK_COUNT - clickCountRef.current;

    // Le niveau d'origine est déjà Debug/Trace : inutile de forcer un mode debug local.
    if (originalLogLevel <= LogLevel.Debug) {
      clickCountRef.current = 0;
      showInfoBubble(t('qcapp_dev_already_active').toString());
      return;
    }

    // Tant que le seuil n'est pas atteint, on informe l'utilisateur du nombre de clics restants.
    if (remainingClicks > 0) {
      if (remainingClicks <= 3) {
        const suffix = remainingClicks > 1 ? 's' : '';
        const messageKey = qcDebugMode ? 'qcapp_dev_disable_clicks' : 'qcapp_dev_enable_clicks';
        showInfoBubble(
          t(messageKey, {
            count: remainingClicks,
            suffix,
          }).toString(),
        );
      }
      return;
    }

    if (clickCountRef.current >= DEBUG_ACTIVATION_CLICK_COUNT) {
      clickCountRef.current = 0;

      const next = !qcDebugMode;
      setQcDebugMode(next);
      if (next) {
        showInfoBubble(t('qcapp_dev_enabled').toString());
      } else {
        showInfoBubble(t('qcapp_dev_disabled').toString());
      }
    }
  }, [originalLogLevel, qcDebugMode, setQcDebugMode, showInfoBubble, t]);

  // Texte version - le suffixe "(debug)" apparaît uniquement en mode debug
  const versionText = `QuickConnect App ${process.env.REACT_APP_VERSION}${envSuffix}${qcDebugMode ? ' (debug)' : ''}`;

  return {
    versionText,
    isDebugMode: qcDebugMode,
    handleVersionClick,
    infoBubbleMessage,
    isInfoBubbleOpen,
    closeInfoBubble,
  };
};

export default useData;
